import { useState, useEffect } from 'react';
import { User } from '../../types/entities';
import { getCurrentUser } from '../../services/authApi';
import { SessionManager } from '../../utils/SessionManager';
import { UseAuthProviderReturn } from './types';
import { toUserEntity } from '../../services/authApi/authApi.types';

/**
 * 認証状態を管理するカスタムフック
 * @returns 認証関連の状態と操作メソッド
 */
export const useAuthProvider = (): UseAuthProviderReturn => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // アプリ初期化時にユーザー情報を取得
  useEffect(() => {
    const loadUser = async (): Promise<void> => {
      try {
        // ストレージからトークンを取得
        const token = SessionManager.getToken();
        
        if (!token) {
          // トークンがなければロード完了
          setLoading(false);
          return;
        }
        
        // APIからユーザー情報を取得
        const userResponse = await getCurrentUser();
        
        if (userResponse && userResponse.id) {
          // APIレスポンスをUser型に変換
          const userEntity = toUserEntity(userResponse);
          setCurrentUser(userEntity);
        }
      } catch (error) {
        // サーバから応答があったエラー（401/403/5xx 等）のときだけセッションを破棄する。
        //
        // ネットワークエラー（レスポンスなし）で破棄してはいけない。Service Worker が
        // アプリシェルを返すようになったため、Tailscale を繋ぎ忘れて PWA を起動しただけで
        // ここに到達するようになった。従来はアプリのコードが動かずトークンは無傷だったので、
        // 同じ扱いを続けるとログイン状態が「起動するたびに消える」体感になる。
        // 認証の正否はサーバ応答があって初めて判定できる、という原則にも合う。
        const hasServerResponse = Boolean((error as { response?: unknown })?.response);
        if (hasServerResponse) {
          SessionManager.clearSession();
        }
      } finally {
        // ロード完了
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  return {
    currentUser,
    loading,
    setCurrentUser
  };
}; 